import {
  Component,
  computed,
  inject,
  input,
  Input,
  OnChanges,
  OnInit,
  output,
  SimpleChanges,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  FormGroupDirective,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '@app/components/base/base.component';
import { MenusServices } from '@app/services/menus.service';
import { NotificationService } from '@app/services/notifications.service';
import { EntitiesServices } from '@app/services/entities.service';
import { FinancialCalculatorService } from '@app/pages/quote/open-quote/services/financial-calculator.service';
import { AuthService } from '@app/common/auth.service';
import {
  DealerBranchOption,
  DealerBranchSyncMode,
  DealerWithBranches,
  FinancialProductEnum,
  openQuoteStatusDisplayLabel,
  resolveDealerBranchSelection,
  selectableDealerBranches,
  statusType,
} from '../model/open-quote.model';
import { DealerBranchCatalogService } from '@app/pages/quote/open-quote/services/dealer-branch-catalog.service';
import { EventEmitter, Output } from '@angular/core';
import { InsuranceService } from '@app/services/insurance.service';
import { environment } from '@env/environment.local';

const STATUS_OPTIONS_APPROVE_FROM_REVIEW: ReadonlyArray<{
  name: string;
  value: string;
}> = [
  {
    name: openQuoteStatusDisplayLabel(statusType.InReview),
    value: statusType.InReview,
  },
  {
    name: openQuoteStatusDisplayLabel(statusType.approved),
    value: statusType.approved,
  },
];

/** Texto de la vista de solo lectura cuando el id no esta en el catalogo. */
const CATALOG_NAME_NOT_FOUND = 'No encontrado';

@Component({
  template: '',
})
export class FinancialServiceOpenQuoteWrapper
  extends BaseComponent
  implements OnInit, OnChanges, ControlValueAccessor, Validator {
  // SERVICIOS
  private readonly fb = inject(FormBuilder);
  private readonly rootFormGroup = inject(FormGroupDirective);
  private readonly entitiesService = inject(EntitiesServices);
  public readonly financialCalculator = inject(FinancialCalculatorService);
  private readonly router = inject(ActivatedRoute);
  public readonly authService = inject(AuthService);
  private readonly insuranceService = inject(InsuranceService);
  private readonly dealerBranchCatalog = inject(DealerBranchCatalogService);
  readonly activeDisableCompanyChange = environment.active_disable_company_change;

  formFinancialService: FormGroup;

  @Output() requestOpenModal: EventEmitter<void> = new EventEmitter();
  @Input() formGroupName: string;
  @Input() userId: any;
  @Input() userName: string;
  readonly changedValue = output<any>();
  public readonly isModeEdition = input.required<boolean>();
  /** Estado final o en revisión: cabecera financiera solo lectura aunque isModeEdition sea false. */
  readonly readOnlyByTerminalStatus = input(false);
  /** Dueño + En revisión: permitir cambiar estado solo a Aprobado. */
  readonly allowStatusApproveFromReview = input(false);
  readonly proposals = input<any[]>([]);
  readonly financialService = input<any>();

  dealers: DealerWithBranches[] = [];
  idQuote: number;
  /** Sucursales seleccionables del distribuidor actual. */
  dealerBranches: DealerBranchOption[] = [];

  public readonly listCompanies = [
    { name: "SOFOM", value: "SOFOM" },
    { name: "DTFS", value: "DTFS" },
  ]

  readonly hasApProposals = computed(() => this.proposals()?.some((proposal) => proposal?.financialProductType === FinancialProductEnum.pureLease));

  constructor(
    route: ActivatedRoute,
    menu: MenusServices,
    notification: NotificationService,
  ) {
    super(route, menu, notification);
  }

  ngOnInit() {
    this.router.params.subscribe((params) => {
      this.createForm();
      this.getDealers();
      this.idQuote = params['id'];
    });
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['financialService'] && this.financialService() != null) {
      if (this.formFinancialService) {
        this.formFinancialService.patchValue({ ...this.financialService() });
        this.formFinancialService.updateValueAndValidity();
        // El patch NO dispara `(ngModelChange)` del combo: sin esto la lista
        // de sucursales queda del render anterior y un par incoherente
        // (típico de un clonado) sobreviviría hasta el guardado.
        this.updateBranches(DealerBranchSyncMode.Hydrate);
      }
    }
    if (changes['userId']) {
      if (this.userId == undefined) return;
      if (this.userId != this.authService.currentUserIds) {
        this.formFinancialService.controls['companyName'].disable();
        this.formFinancialService.controls['quoteName'].disable();
        this.formFinancialService.controls['daysOfValidity'].disable();
        this.formFinancialService.controls['dealerId'].disable();
        this.formFinancialService.controls['dealerBranchId'].disable();
        this.formFinancialService.controls['statusType'].disable();
      } else {
        this.syncStatusTypeEnabled();
      }
    }
    if (changes['allowStatusApproveFromReview']) {
      this.syncStatusTypeEnabled();
    }
  }

  private syncStatusTypeEnabled(): void {
    const statusCtrl = this.formFinancialService?.controls?.['statusType'];
    if (!statusCtrl) return;
    if (this.showStatusSelectInReadOnly) {
      statusCtrl.enable({ emitEvent: false });
    }
  }


  createForm() {
    this.formFinancialService = this.rootFormGroup.control.get(
      this.formGroupName
    ) as FormGroup;

    this.formFinancialService.controls['dealerId'].valueChanges.subscribe((value) => {
      this.insuranceService.dealerId.set(value);
    });
    this.formFinancialService.controls['dealerBranchId'].valueChanges.subscribe((value) => {
      this.insuranceService.dealerBranchId.set(value);
    });

    if (this.formFinancialService && this.financialService() !== undefined && this.financialService() !== null) {
      this.formFinancialService.patchValue({ ...this.financialService() });
      this.formFinancialService.updateValueAndValidity();
    }
    this.syncStatusTypeEnabled();
  }

  getDealers() {
    this.dealerBranches = [];
    this.entitiesService.listDealers().subscribe((r) => {
      this.dealers = (r?.data ?? []).filter(
        (dealer: { enabled: boolean }) => dealer?.enabled === true
      );
      // El guardado valida el par contra este mismo catálogo.
      this.dealerBranchCatalog.setDealers(this.dealers);
      // Hidratación: el catálogo puede llegar después del detalle de la
      // cotización, y recién acá se puede saber si la sucursal es válida.
      this.updateBranches(DealerBranchSyncMode.Hydrate);
    });
  }

  /**
   * Única puerta de sincronización de la sucursal con el distribuidor.
   *
   * Por defecto resuelve como cambio de distribuidor, que es como la invoca
   * el `(ngModelChange)` del combo en el template.
   */
  updateBranches(
    mode: DealerBranchSyncMode = DealerBranchSyncMode.DealerChange
  ): void {
    const branchControl =
      this.formFinancialService?.controls?.['dealerBranchId'];
    if (!branchControl) {
      return;
    }
    // Sin catálogo no hay con qué decidir: se conserva lo que traiga el
    // control y `getDealers()` vuelve a resolver cuando responda.
    if (!this.dealers?.length) {
      this.dealerBranches = [];
      return;
    }
    const dealerId = this.formFinancialService.controls['dealerId']?.value;
    const branches = selectableDealerBranches(this.dealers, dealerId);
    const selection = resolveDealerBranchSelection({
      branches,
      currentBranchId: branchControl.value,
      mode,
    });

    this.dealerBranches = branches;
    if (selection.branchId !== branchControl.value) {
      branchControl.setValue(selection.branchId);
    }
    if (selection.disabled) {
      branchControl.disable({ emitEvent: false });
    } else {
      branchControl.enable({ emitEvent: false });
    }
  }

  getNameById(id: number | string | null | undefined, type: 'dealer' | 'branch'): string {
    if (id == null || id === '') {
      return CATALOG_NAME_NOT_FOUND;
    }
    if (type === 'dealer') {
      const dealer = this.dealers?.find?.((dealer: any) => dealer?.id == id);
      if (dealer) {
        return dealer.name ?? CATALOG_NAME_NOT_FOUND;
      }
      const branchId = this.formFinancialService?.controls?.['dealerBranchId']?.value;
      if (branchId != null) {
        for (const d of this.dealers ?? []) {
          const branch = d?.dealerBranches?.find?.((b: any) => b?.id == branchId);
          if (branch) {
            return d?.name ?? CATALOG_NAME_NOT_FOUND;
          }
        }
      }
      return CATALOG_NAME_NOT_FOUND;
    }
    const dealers = this.dealers ?? [];
    for (const dealer of dealers) {
      const branch = dealer?.dealerBranches?.find?.(
        (branch: any) => branch?.id == id
      );
      if (branch) {
        return branch?.name ?? CATALOG_NAME_NOT_FOUND;
      }
    }
    return CATALOG_NAME_NOT_FOUND;
  }

  getStatus(status: statusType | string | null | undefined) {
    return openQuoteStatusDisplayLabel(status == null ? '' : String(status));
  }



  onChangeCompany() {
    if (this.activeDisableCompanyChange) {
      const beforeValue = this.formFinancialService.controls['companyName'].value === "SOFOM" ? "DTFS" : "SOFOM"
      if (this.hasApProposals()) {
        this.showNotificationError("No es posible el cambio de compañía - La cotizacion contiene propuesta de Arrendamiento puro");
        this.formFinancialService.controls['companyName'].setValue(beforeValue);
        return;
      }
    }
  }

  //#region CVA
  writeValue(value: any): void {
    if (value) {
      this.formFinancialService.setValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  private onChange = (_: any) => { };
  private onTouched = () => { };

  //#endregion

  //#region Validator
  validate(_control: AbstractControl<any, any>): ValidationErrors | null {
    if (!this.formFinancialService.controls['dealerBranchId']?.value) {
      return { dealerBranchRequired: true };
    }
    return this.formFinancialService.valid
      ? null
      : { invalidFormFinancialService: true };
  }

  triggerModal() {
    this.requestOpenModal.emit();
  }

  onCustomerTypeChange() {
    this.requestOpenModal.emit();
  }

  onInputFocus() {
    this.requestOpenModal.emit();
  }

  //#endregion

  get dealerId() {
    return this.formFinancialService.controls['dealerId'].value;
  }

  get dealerBranchId() {
    return this.formFinancialService.controls['dealerBranchId'].value;
  }

  get isEditable() {
    return (this.userId == 0 ||
      this.userId == this.authService.currentUserIds ||
      this.userId == null) &&
      !this.isModeEdition() &&
      !this.readOnlyByTerminalStatus()
  }

  /** Select de estado en vista solo lectura (En revisión → Aprobado). */
  get showStatusSelectInReadOnly(): boolean {
    return (
      this.allowStatusApproveFromReview() &&
      !this.showReadonlyQuoteBelongsToOtherUser &&
      !this.isModeEdition()
    );
  }

  get statusSelectOptions(): ReadonlyArray<{ name: string; value: string }> {
    if (this.showStatusSelectInReadOnly) {
      return STATUS_OPTIONS_APPROVE_FROM_REVIEW;
    }
    return this.financialCalculator.statusType;
  }

  /** Cartel «pertenece a…»: solo si la cotización es de otro usuario (no cuando el dueño la ve en solo lectura). */
  get showReadonlyQuoteBelongsToOtherUser(): boolean {
    if (this.userId == null || this.userId === '') {
      return false;
    }
    return String(this.userId) !== String(this.authService.currentUserIds);
  }

  get hasBranches() {
    return this.dealerBranches?.length > 0;
  }
}
